const base64UrlToUint8Array = (value) => {
    const normalizedValue = `${value ?? ""}`
        .replace(/-/g, "+")
        .replace(/_/g, "/");
    const padding = normalizedValue.length % 4 === 0
        ? ""
        : "=".repeat(4 - (normalizedValue.length % 4));
    const binaryValue = window.atob(`${normalizedValue}${padding}`);
    const bytes = new Uint8Array(binaryValue.length);

    for (let index = 0; index < binaryValue.length; index += 1) {
        bytes[index] = binaryValue.charCodeAt(index);
    }

    return bytes;
};

const arrayBufferToBase64Url = (value) => {
    if (!value) {
        return null;
    }

    const bytes = value instanceof ArrayBuffer
        ? new Uint8Array(value)
        : value instanceof Uint8Array
            ? value
            : new Uint8Array(value.buffer);

    let binaryValue = "";

    bytes.forEach((byte) => {
        binaryValue += String.fromCharCode(byte);
    });

    return window.btoa(binaryValue)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/g, "");
};

const mapCredentialDescriptor = (descriptor) => ({
    ...descriptor,
    id: base64UrlToUint8Array(descriptor.id)
});

const normalizeCreationOptions = (options) => ({
    ...options,
    challenge: base64UrlToUint8Array(options.challenge),
    user: {
        ...options.user,
        id: base64UrlToUint8Array(options.user.id)
    },
    excludeCredentials: options.excludeCredentials?.map(mapCredentialDescriptor) ?? []
});

const normalizeRequestOptions = (options) => ({
    ...options,
    challenge: base64UrlToUint8Array(options.challenge),
    allowCredentials: options.allowCredentials?.map(mapCredentialDescriptor) ?? []
});

const parseOptionsJson = (optionsJson) => {
    if (!optionsJson) {
        throw new Error("The server did not return valid passkey options.");
    }

    return typeof optionsJson === "string"
        ? JSON.parse(optionsJson)
        : optionsJson;
};

const ensurePasskeysSupported = () => {
    if (typeof window === "undefined" || !window.PublicKeyCredential || !navigator?.credentials) {
        throw new Error("Passkeys are not supported in this browser.");
    }
};

const getCreationOptions = (optionsJson) => {
    const parsedOptions = parseOptionsJson(optionsJson);
    const browserPublicKeyCredential = window.PublicKeyCredential;

    if (typeof browserPublicKeyCredential?.parseCreationOptionsFromJSON === "function") {
        return browserPublicKeyCredential.parseCreationOptionsFromJSON(parsedOptions);
    }

    return normalizeCreationOptions(parsedOptions);
};

const getRequestOptions = (optionsJson) => {
    const parsedOptions = parseOptionsJson(optionsJson);
    const browserPublicKeyCredential = window.PublicKeyCredential;

    if (typeof browserPublicKeyCredential?.parseRequestOptionsFromJSON === "function") {
        return browserPublicKeyCredential.parseRequestOptionsFromJSON(parsedOptions);
    }

    return normalizeRequestOptions(parsedOptions);
};

const serializeAttestationCredential = (credential) => ({
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type,
    extensions: credential.getClientExtensionResults?.() ?? {},
    response: {
        attestationObject: arrayBufferToBase64Url(credential.response.attestationObject),
        clientDataJSON: arrayBufferToBase64Url(credential.response.clientDataJSON),
        transports: typeof credential.response.getTransports === "function"
            ? credential.response.getTransports()
            : []
    }
});

const serializeAssertionCredential = (credential) => ({
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type,
    extensions: credential.getClientExtensionResults?.() ?? {},
    response: {
        authenticatorData: arrayBufferToBase64Url(credential.response.authenticatorData),
        clientDataJSON: arrayBufferToBase64Url(credential.response.clientDataJSON),
        signature: arrayBufferToBase64Url(credential.response.signature),
        userHandle: credential.response.userHandle
            ? arrayBufferToBase64Url(credential.response.userHandle)
            : null
    }
});

const toPasskeyError = (error, fallbackMessage) => {
    const message = error?.name === "NotAllowedError"
        ? "The passkey request was cancelled or timed out."
        : error?.name === "InvalidStateError"
            ? "This device is already registered for that account."
            : error?.name === "NotSupportedError"
                ? "This browser does not support passkeys for this action."
                : error?.name === "SecurityError"
                    ? "Passkeys require HTTPS or a trusted local development origin."
                    : error?.message || fallbackMessage;

    const normalizedError = new Error(message);
    normalizedError.name = error?.name || "PasskeyError";
    return normalizedError;
};

export const registerPasskey = async (optionsJson) => {
    ensurePasskeysSupported();

    try {
        const credential = await navigator.credentials.create({
            publicKey: getCreationOptions(optionsJson)
        });

        if (!credential) {
            throw new Error("No passkey credential was created.");
        }

        return serializeAttestationCredential(credential);
    } catch (error) {
        throw toPasskeyError(error, "Unable to create a passkey right now.");
    }
};

export const authenticateWithPasskey = async (optionsJson) => {
    ensurePasskeysSupported();

    try {
        const credential = await navigator.credentials.get({
            publicKey: getRequestOptions(optionsJson)
        });

        if (!credential) {
            throw new Error("No passkey assertion was returned.");
        }

        return serializeAssertionCredential(credential);
    } catch (error) {
        throw toPasskeyError(error, "Unable to verify your passkey right now.");
    }
};

export const supportsPasskeys = () => {
    return typeof window !== "undefined"
        && !!window.PublicKeyCredential
        && !!navigator?.credentials;
};
